import { sb } from './supabase';
import { calcOptionRecurringCosts, calcOneTimeTotals } from './costEngine';
import { calcROM } from './rom';

export const OPTION_STATUS_LABEL = {
  draft: 'Draft',
  in_assessment: 'In assessment',
  assessed: 'Assessed',
  selected: 'Selected',
  rejected: 'Rejected',
};

export const OPTION_WORKSPACE_TABS = [
  { key: 'platforms', label: 'Platform mapping' },
  { key: 'fitgap', label: 'Fit-gap assessment' },
  { key: 'integrations', label: 'Integrations' },
  { key: 'architecture', label: 'Architecture' },
  { key: 'rom', label: 'ROM & costs' },
];

export const MAX_OPTIONS = 3;

/** Ensure programme has a platform selection session; return session id. */
export async function ensurePlatformSession(programmeId, profileId, projectId = null) {
  const { data: existing } = await sb
    .from('platform_selection_sessions')
    .select('id')
    .eq('programme_id', programmeId)
    .maybeSingle();

  if (existing?.id) {
    if (projectId) {
      await sb.from('platform_selection_sessions')
        .update({ project_id: projectId })
        .eq('id', existing.id)
        .is('project_id', null);
    }
    return existing.id;
  }

  const { data, error } = await sb
    .from('platform_selection_sessions')
    .insert({
      programme_id: programmeId,
      project_id: projectId,
      status: 'in_progress',
      current_step: 'architecture',
      created_by: profileId || null,
    })
    .select('id')
    .single();
  if (error) throw error;
  return data.id;
}

export async function fetchProjectOptions(projectId, programmeId) {
  const { data: session } = await sb
    .from('platform_selection_sessions')
    .select('id')
    .eq('programme_id', programmeId)
    .maybeSingle();
  if (!session?.id) return [];

  const { data, error } = await sb
    .from('architecture_options')
    .select('*')
    .eq('session_id', session.id)
    .eq('project_id', projectId)
    .neq('status', 'rejected')
    .order('order_index');
  if (error) throw error;
  return data || [];
}

export async function fetchOptionDetail(optionId) {
  const { data: option, error } = await sb
    .from('architecture_options')
    .select('*, platform_selection_sessions(programme_id)')
    .eq('id', optionId)
    .maybeSingle();
  if (error) throw error;
  return option;
}

export async function fetchOptionAssessments(optionId, projectId) {
  const [{ data: reqs }, { data: assessments }, { data: candidates }] = await Promise.all([
    sb.from('requirements').select('*').eq('project_id', projectId).order('order_index'),
    sb.from('requirement_option_assessments').select('*').eq('architecture_option_id', optionId),
    sb.from('platform_candidates')
      .select('*, solution_domains(name)')
      .eq('session_id', (
        await sb.from('architecture_options').select('session_id').eq('id', optionId).maybeSingle()
      ).data?.session_id),
  ]);

  const assessmentByReq = new Map((assessments || []).map((a) => [a.requirement_id, a]));
  return {
    requirements: reqs || [],
    assessments: assessmentByReq,
    candidates: candidates || [],
  };
}

export async function upsertAssessment(optionId, requirementId, patch, profileId) {
  const payload = {
    architecture_option_id: optionId,
    requirement_id: requirementId,
    ...patch,
    sized_by: patch.t_shirt_size ? profileId : patch.sized_by,
    sized_at: patch.t_shirt_size ? new Date().toISOString() : patch.sized_at,
    updated_at: new Date().toISOString(),
  };
  const { error } = await sb.from('requirement_option_assessments').upsert(payload, {
    onConflict: 'requirement_id,architecture_option_id',
  });
  if (error) throw error;
}

export function fitGapSummary(assessments, requirements) {
  const counts = { ootb: 0, config: 0, enhancement: 0, custom_dev: 0, unset: 0 };
  for (const req of requirements) {
    const a = assessments.get?.(req.id) || assessments[req.id];
    const cls = a?.ootb_classification;
    if (cls && counts[cls] !== undefined) counts[cls] += 1;
    else counts.unset += 1;
  }
  const assessed = requirements.length - counts.unset;
  const ootbLike = counts.ootb + counts.config;
  const score = requirements.length ? Math.round((ootbLike / requirements.length) * 100) : null;
  return { counts, assessed, score };
}

export async function fetchOptionCostData(optionId, projectId, programmeId) {
  const [recRes, otRes, bcRes, projRes] = await Promise.all([
    sb.from('recurring_cost_lines').select('*, cost_types(category, name, default_escalation_pct)').eq('architecture_option_id', optionId),
    sb.from('one_time_cost_lines').select('*, cost_types(name, category)').eq('architecture_option_id', optionId),
    sb.from('business_cases').select('*').eq('architecture_option_id', optionId).order('version_number', { ascending: false }).limit(1).maybeSingle(),
    sb.from('projects').select('default_cost_escalation_pct').eq('id', projectId).maybeSingle(),
  ]);

  const escalation = projRes.data?.default_cost_escalation_pct ?? 3;
  const recurring = calcOptionRecurringCosts(recRes.data || [], { defaultEscalationPct: escalation });
  const oneTime = calcOneTimeTotals(otRes.data || []);

  return {
    recurringLines: recRes.data || [],
    oneTimeLines: otRes.data || [],
    businessCase: bcRes.data,
    recurring,
    oneTime,
    escalation,
  };
}

export async function fetchOptionRomSnapshot(optionId, projectId, programmeId, overrides = {}) {
  const [{ data: assessments }, { data: reqs }, { data: ifaces }] = await Promise.all([
    sb.from('requirement_option_assessments').select('*').eq('architecture_option_id', optionId),
    sb.from('requirements').select('*').eq('project_id', projectId).in('status', ['approved', 'baselined', 'sized']),
    sb.from('interfaces').select('*').eq('architecture_option_id', optionId),
  ]);

  const assessmentMap = new Map((assessments || []).map((a) => [a.requirement_id, a]));
  const sizedReqs = (reqs || []).map((r) => {
    const a = assessmentMap.get(r.id);
    return {
      ...r,
      ootb_classification: a?.ootb_classification ?? r.ootb_classification,
      t_shirt_size: a?.t_shirt_size ?? r.t_shirt_size,
    };
  }).filter((r) => r.t_shirt_size);

  return calcROM({ requirements: sizedReqs, interfaces: ifaces || [], overrides });
}

/** Check Define gate bake-off exit criteria for auto-suggest on Stage Gates screen. */
export async function fetchBakeOffGateStatus(projectId, programmeId) {
  const [{ data: project }, { data: options }, { data: decision }, { data: canonical }] = await Promise.all([
    sb.from('projects').select('requirements_baseline_locked_at').eq('id', projectId).maybeSingle(),
    sb.from('architecture_options').select('id, status').eq('project_id', projectId).neq('status', 'rejected'),
    sb.from('governance_decisions')
      .select('id')
      .eq('project_id', projectId)
      .eq('decision_type', 'scenario_selection')
      .eq('status', 'made')
      .limit(1)
      .maybeSingle(),
    sb.from('solution_architecture_documents')
      .select('id')
      .eq('project_id', projectId)
      .eq('is_canonical', true)
      .limit(1)
      .maybeSingle(),
  ]);

  const assessedCount = (options || []).filter((o) => ['assessed', 'selected'].includes(o.status)).length;
  const hasRom = await sb
    .from('business_cases')
    .select('id')
    .eq('project_id', projectId)
    .not('architecture_option_id', 'is', null)
    .limit(1)
    .maybeSingle();

  return {
    baselineLocked: !!project?.requirements_baseline_locked_at,
    optionsAssessed: assessedCount > 0,
    scenarioDecision: !!decision?.id,
    canonicalArchitecture: !!canonical?.id,
    hasBusinessCase: !!hasRom.data?.id,
  };
}

/** Map of requirement_id → assessment for the selected winning option (if any). */
export async function fetchWinningOptionAssessmentMap(projectId) {
  const { data: option } = await sb
    .from('architecture_options')
    .select('id')
    .eq('project_id', projectId)
    .eq('status', 'selected')
    .maybeSingle();
  if (!option?.id) return new Map();

  const { data } = await sb
    .from('requirement_option_assessments')
    .select('requirement_id, ootb_classification, t_shirt_size')
    .eq('architecture_option_id', option.id);
  return new Map((data || []).map((a) => [a.requirement_id, a]));
}

/** Apply per-option assessments over requirement rows when a winner exists. */
export function applyOptionAssessments(requirements, assessmentMap) {
  if (!assessmentMap?.size) return requirements;
  return requirements.map((r) => {
    const a = assessmentMap.get(r.id);
    if (!a) return r;
    return {
      ...r,
      ootb_classification: a.ootb_classification ?? r.ootb_classification,
      t_shirt_size: a.t_shirt_size ?? r.t_shirt_size,
    };
  });
}

/** Promote winning option after scenario decision. */
export async function promoteWinningOption(optionId, projectId, programmeId, profileId) {
  await sb.from('architecture_options')
    .update({ status: 'selected', is_winner: true, is_selected: true })
    .eq('id', optionId);

  await sb.from('architecture_options')
    .update({ status: 'rejected', is_winner: false })
    .eq('project_id', projectId)
    .neq('id', optionId)
    .neq('status', 'rejected');

  await sb.from('solution_architecture_documents')
    .update({ is_canonical: false })
    .eq('project_id', projectId);

  const { data: doc } = await sb
    .from('solution_architecture_documents')
    .select('id')
    .eq('architecture_option_id', optionId)
    .maybeSingle();

  if (doc?.id) {
    await sb.from('solution_architecture_documents')
      .update({ is_canonical: true, project_id: projectId })
      .eq('id', doc.id);
  }

  const sessionRes = await sb
    .from('platform_selection_sessions')
    .select('id')
    .eq('programme_id', programmeId)
    .maybeSingle();

  if (sessionRes.data?.id) {
    await sb.from('platform_selection_sessions')
      .update({
        winner_architecture_option_id: optionId,
        selected_architecture_option_id: optionId,
        status: 'approved',
        approved_at: new Date().toISOString(),
        approved_by: profileId || null,
      })
      .eq('id', sessionRes.data.id);
  }
}

/** Ensure a business case row exists for an option during bake-off. */
export async function ensureBusinessCaseForOption(optionId, projectId, programmeId, profileId) {
  const { data: existing } = await sb
    .from('business_cases')
    .select('id')
    .eq('architecture_option_id', optionId)
    .order('version_number', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (existing?.id) return existing.id;

  const { data, error } = await sb
    .from('business_cases')
    .insert({
      programme_id: programmeId,
      project_id: projectId,
      architecture_option_id: optionId,
      version_number: 1,
      version_name: 'ROM v1',
      status: 'draft',
      cost_currency: 'GBP',
      created_by: profileId || null,
    })
    .select('id')
    .single();
  if (error) throw error;
  return data.id;
}

/** Persist cached cost totals on business_cases via Postgres function. */
export async function refreshBusinessCaseTotals(businessCaseId) {
  if (!businessCaseId) return;
  const { error } = await sb.rpc('refresh_business_case_cost_totals', {
    p_business_case_id: businessCaseId,
  });
  if (error) throw error;
}
